import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent, updateUserProgress } from '../../services/funnelService';

const Container = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px); /* Extra space to ensure CTA is scrollable + 20px buffer */
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.lg};
  min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 120px); /* Ensure content is tall enough to scroll */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 80px);
  }
`;

const Title = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
`;

const ContinueButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  /* Button is now in content flow, adjust spacing */
  margin-top: 0;
  margin-bottom: 24px;
  /* Better width handling for longer text */
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  /* Multi-line text support for very long text */
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  /* Ensure proper touch targets on mobile */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }

  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem;
    min-width: 150px;
  }
`;

const NoDataMessage = styled.div`
  background: ${theme.colors.neutral}20;
  border: 2px solid ${theme.colors.neutral}40;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin: ${theme.spacing.lg} 0;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const NoDataTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const NoDataText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
`;

// Pricing Slider Components
const PricingContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  /* Allow scrolling when not interacting with slider */
  @media (max-width: ${theme.breakpoints.tablet}) {
    touch-action: pan-y;
  }
`;

const PricingTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
  text-align: center;
`;

const SliderContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: ${theme.spacing.md} 0 0 0;
  touch-action: pan-y;
`;

const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  cursor: pointer;
  
  /* Mobile touch optimization */
  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 80px; /* Height to accommodate thumb */
  }
`;

const SliderTrack = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.neutral}40;
  border-radius: ${theme.borderRadius.lg};
  position: relative;
  
  /* Mobile touch optimization */
  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 12px; /* Slightly thicker track for easier interaction */
  }
`;

const SliderDots = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  pointer-events: none;
  z-index: 2;
`;

const SliderDot = styled.div`
  width: 6px;
  height: 6px;
  background: ${theme.colors.doryLighterBlue};
  border-radius: 50%;
  opacity: 0.8;
`;

const SliderFill = styled.div`
  height: 100%;
  background: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.lg};
  width: ${props => props.width}%;
  transition: width ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
`;

const SliderThumb = styled.div`
  position: absolute;
  top: 50%;
  left: ${props => props.position}%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  background: ${theme.colors.primary};
  border: 3px solid white;
  border-radius: 50%;
  box-shadow: ${theme.shadows.md};
  transition: left ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
  cursor: pointer;
  touch-action: none;
  user-select: none;
  
  /* Larger invisible touch target for mobile */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
    background: transparent;
    border-radius: 50%;
    z-index: -1;
  }
  
  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: ${theme.shadows.lg};
  }
  
  &:active {
    transform: translate(-50%, -50%) scale(1.05);
  }
  
  /* Dragging state */
  ${props => props.isDragging && `
    transform: translate(-50%, -50%) scale(1.15);
    box-shadow: ${theme.shadows.xl};
    border-color: white; /* Maintain white border during dragging */
  `}
  
  /* Mobile touch optimization */
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 60px; /* Smaller thumb for better mobile interaction */
    height: 60px;
    border-width: 4px;
    
    &::before {
      width: 100px; /* Touch target for easier grabbing */
      height: 100px;
    }
  }
`;

const PriceDisplay = styled.div`
  margin-top: ${theme.spacing.md};
  text-align: center;
`;

const PriceAmount = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.regular};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
`;

const SpecialMessage = styled.div`
  background: ${theme.colors.primary}20;
  border: 2px solid ${theme.colors.primary}40;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  margin: ${theme.spacing.md} 0;
  width: 100%;
  max-width: 80vw;
  text-align: center;
`;

const SpecialMessageText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  font-weight: ${theme.typography.weights.medium};
  margin: 0;
`;

// Win Rate Visualization Components (reused from OptimalPaceRaceScreen)
const WinRateContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: ${theme.spacing.md} auto 38px auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WinRateTitle = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
  text-align: center;
`;

const WinRateChart = styled.div`
  width: 100%;
  max-width: 80vw;
  height: 200px;
  position: relative;
  background: ${theme.colors.surface};
  padding: ${theme.spacing.md};
  box-sizing: border-box;
`;

const WinRateBar = styled.div`
  position: absolute;
  bottom: 0;
  width: ${props => props.width}%;
  height: ${props => props.height}%;
  background: ${props => {
    if (props.isTargetRange) return theme.colors.doryLighterBlue; // Light blue for target range
    return `${theme.colors.doryGreen}80`; // Light green for others
  }};
  border-radius: ${theme.borderRadius.sm} ${theme.borderRadius.sm} 0 0;
  transition: all ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
  left: ${props => props.left}%;
  
  &:hover {
    transform: scaleY(1.05);
    transform-origin: bottom;
  }
`;

const WinRateLabel = styled.div`
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${theme.typography.bodyFont};
  font-size: 12px;
  color: ${theme.colors.textLight};
  font-weight: ${props => props.isBold ? theme.typography.weights.bold : theme.typography.weights.regular};
`;

const WinRateValue = styled.div`
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${theme.typography.bodyFont};
  font-size: calc(${theme.typography.sizes.small} * 0.8);
  color: ${theme.colors.text};
  font-weight: ${props => props.isBold ? theme.typography.weights.bold : theme.typography.weights.regular};
`;

const SVGLine = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
`;

const MembershipSelectionScreen = ({ data, onNext, userProgress = {} }) => {
  const [optimalPaceData, setOptimalPaceData] = useState(userProgress.calculatedMetrics?.optimalPace);
  const [selectedPrice, setSelectedPrice] = useState(0); // Default to $0 (current Optimal Pace)
  const [initialSelectionSaved, setInitialSelectionSaved] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  // Update optimal pace data when userProgress changes
  useEffect(() => {
    const newOptimalPaceData = userProgress.calculatedMetrics?.optimalPace;
    if (newOptimalPaceData) {
      console.log('MembershipSelectionScreen: Received optimal pace data', newOptimalPaceData);
      setOptimalPaceData(newOptimalPaceData);
      
      // Only set initial price if it hasn't been set yet (prevents reset on re-renders)
      if (selectedPrice === 0 && !initialSelectionSaved) {
        // Set default to 1 point position ($19)
        const currentPace = Math.round(newOptimalPaceData.optimalHours);
        
        // Default to $19 (1 point position) for all users
        setSelectedPrice(19);
        // Save initial selection
        setTimeout(() => {
          saveMembershipSelection(19);
          setInitialSelectionSaved(true);
        }, 100);
      }
    }
  }, [userProgress, selectedPrice, initialSelectionSaved]);

  // Process merge fields and format text content
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  const currentOptimalPace = optimalPaceData?.optimalHours ? Math.round(optimalPaceData.optimalHours) : null;

  // Calculate target optimal pace based on selected price
  // $0 = current Optimal Pace, each $19 increment = +1 Optimal Pace
  const targetOptimalPace = Math.min(18, currentOptimalPace + Math.floor(selectedPrice / 19));

  // Handle slider drag functionality
  const handleSliderDrag = (e, maxPrice, step) => {
    const sliderWrapper = document.getElementById('membership-slider-wrapper');
    if (!sliderWrapper) return;
    
    const rect = sliderWrapper.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clickX = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    
    // Calculate the number of steps and snap to nearest step
    const totalSteps = maxPrice / step;
    const stepPosition = Math.round(percentage * totalSteps);
    const newPrice = Math.min(maxPrice, stepPosition * step);
    
    setSelectedPrice(newPrice);
    
    // Save membership selection to Firebase
    saveMembershipSelection(newPrice);
  };

  // Handle mouse/touch events for the thumb
  const handleThumbMouseDown = (e, maxPrice, step) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    
    const handleMouseMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleSliderDrag(e, maxPrice, step);
    };
    const handleMouseUp = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleThumbTouchStart = (e, maxPrice, step) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    
    const handleTouchMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleSliderDrag(e, maxPrice, step);
    };
    const handleTouchEnd = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  };

  // Handle clicking on the bar track
  const handleBarClick = (e, maxPrice, step) => {
    e.preventDefault();
    e.stopPropagation();
    handleSliderDrag(e, maxPrice, step);
  };

  const handleBarTouchStart = (e, maxPrice, step) => {
    e.preventDefault();
    e.stopPropagation();
    handleSliderDrag(e, maxPrice, step);
  };


  // Save membership selection to Firebase
  const saveMembershipSelection = async (price) => {
    try {
      if (!userProgress.shortcode) {
        console.warn('No shortcode available to save membership selection');
        return;
      }

      // Calculate target Optimal Pace and points to improve
      const calculatedTargetOptimalPace = Math.min(18, currentOptimalPace + Math.floor(price / 19));
      
      const membershipData = {
        membershipSelection: {
          selectedPrice: price,
          monthlyAmount: price,
          currentOptimalPace: currentOptimalPace,
          targetOptimalPace: calculatedTargetOptimalPace,
          pointsToImprove: calculatedTargetOptimalPace - currentOptimalPace,
          winRateImprovement: {
            from: Math.round((currentOptimalPace / 18) * 100),
            to: Math.round((calculatedTargetOptimalPace / 18) * 100)
          },
          timestamp: new Date()
        }
      };

      await updateUserProgress(userProgress.shortcode, membershipData);
      console.log('Membership selection saved:', membershipData);
    } catch (error) {
      console.error('Error saving membership selection:', error);
    }
  };

  // Helper function to render the pricing slider
  const renderPricingSlider = () => {
    const minPrice = 0; // $0 = current Optimal Pace
    
    // Special case: if Optimal Pace is 18, only allow $0 and $19 positions
    let maxPrice, step;
    if (currentOptimalPace === 18) {
      maxPrice = 19; // Only $0 and $19 positions
      step = 19;
    } else {
      // Calculate max price to ensure target Optimal Pace doesn't exceed 18
      const maxGrowth = 18 - currentOptimalPace;
      maxPrice = maxGrowth * 19;
      step = 19;
    }
    
    const displayPrice = selectedPrice;

    return (
      <PricingContainer>
        <SliderContainer>
          <SliderWrapper 
            id="membership-slider-wrapper"
            onClick={(e) => handleBarClick(e, maxPrice, step)}
            onTouchStart={(e) => handleBarTouchStart(e, maxPrice, step)}
          >
            <SliderTrack>
              <SliderFill width={maxPrice > 0 ? (selectedPrice / maxPrice) * 100 : 0} />
              <SliderDots>
                {Array.from({ length: Math.floor(maxPrice / step) + 1 }, (_, index) => (
                  <SliderDot key={index} />
                ))}
              </SliderDots>
              <SliderThumb 
                position={maxPrice > 0 ? (selectedPrice / maxPrice) * 100 : 0}
                isDragging={isDragging}
                onMouseDown={(e) => handleThumbMouseDown(e, maxPrice, step)}
                onTouchStart={(e) => handleThumbTouchStart(e, maxPrice, step)}
              />
            </SliderTrack>
          </SliderWrapper>
          <PriceDisplay>
            <PriceAmount
              dangerouslySetInnerHTML={{
                __html: currentOptimalPace === 18
                  ? "Keep your Optimal Pace at 18 and you'll only pay $1 per month for your membership."
                  : selectedPrice === 0 
                    ? "If my Optimal Pace does not increase, my membership will continue to cost only $1 per month."
                    : `After I increase my Optimal Pace by <strong>${targetOptimalPace - currentOptimalPace} ${targetOptimalPace - currentOptimalPace === 1 ? 'point' : 'points'}</strong>, my membership will cost $${displayPrice} per month.`
              }}
            />
          </PriceDisplay>
        </SliderContainer>
      </PricingContainer>
    );
  };

  // Helper function to render the win rate visualization
  const renderWinRateVisualization = () => {
    if (optimalPaceData?.optimalHours === null || optimalPaceData?.optimalHours === undefined) return null;

    const currentPace = Math.round(optimalPaceData.optimalHours);
    
    // Calculate the range of bars to show:
    // - Always show all bars from Optimal Pace up to 18
    // - When Optimal Pace is 15 or higher, show enough bars to the left to ensure 6 total bars
    const endPace = 18;
    const barsToRight = endPace - currentPace + 1; // +1 to include current pace
    
    let startPace;
    if (currentPace >= 15) {
      // For Optimal Pace 15+, show 6 total bars
      startPace = Math.max(0, currentPace - (6 - barsToRight));
    } else {
      // For Optimal Pace < 15, show one bar to the left (if possible)
      startPace = Math.max(0, currentPace - 1);
    }
    
    const totalBars = endPace - startPace + 1;
    const barWidth = 100 / totalBars;
    
    return (
      <WinRateContainer>
        <WinRateChart>
          {/* Render bars from startPace to endPace */}
          {Array.from({ length: totalBars }, (_, index) => {
            const pace = startPace + index;
            const winRate = Math.round((pace / 18) * 100);
            const barHeight = winRate;
            const barLeft = (index * barWidth);
            const isCurrentPace = pace === currentPace;
            const isTargetRange = pace >= currentPace && pace <= targetOptimalPace;
            
            return (
              <WinRateBar
                key={pace}
                width={barWidth - 1} // Small gap between bars
                height={barHeight}
                left={barLeft}
                isTargetRange={isTargetRange}
              >
                <WinRateValue isBold={isCurrentPace}>{winRate}%</WinRateValue>
                <WinRateLabel isBold={isCurrentPace}>{pace}</WinRateLabel>
              </WinRateBar>
            );
          })}
          
          {/* Improvement line connecting current pace to target pace */}
          {selectedPrice > 0 && currentPace !== 18 && (() => {
            // Calculate horizontal centers - account for bar positioning and gaps
            const currentBarIndex = currentPace - startPace;
            const targetBarIndex = targetOptimalPace - startPace;
            const currentBarCenter = (currentBarIndex * barWidth) + ((barWidth - 1) / 2);
            const targetBarCenter = (targetBarIndex * barWidth) + ((barWidth - 1) / 2);
            
            // Calculate vertical centers (middle of each bar's height)
            const currentWinRate = Math.round((currentPace / 18) * 100);
            const targetWinRate = Math.round((targetOptimalPace / 18) * 100);
            
            // Bars are positioned from bottom: 0, so top is at (100 - barHeight)
            // Position line endpoints inside the bars, with right endpoint adjusted to reduce slope slightly
            const currentBarTop = 100 - currentWinRate + (currentWinRate * 0.25); // 25% down from top of current bar
            const targetBarTop = 100 - targetWinRate + (targetWinRate * 0.15); // 15% down from top of target bar (slightly reduced slope)
            
            return (
              <SVGLine>
                <line
                  x1={`${currentBarCenter}%`}
                  y1={`${currentBarTop}%`}
                  x2={`${targetBarCenter}%`}
                  y2={`${targetBarTop}%`}
                  stroke={theme.colors.doryDarkerBlue}
                  strokeWidth="6"
                />
                <circle
                  cx={`${currentBarCenter}%`}
                  cy={`${currentBarTop}%`}
                  r="6"
                  fill={theme.colors.doryDarkerBlue}
                />
                <circle
                  cx={`${targetBarCenter}%`}
                  cy={`${targetBarTop}%`}
                  r="6"
                  fill={theme.colors.doryDarkerBlue}
                />
              </SVGLine>
            );
          })()}
        </WinRateChart>
      </WinRateContainer>
    );
  };

  // Check if we have optimal pace data
  if (!optimalPaceData) {
    return (
      <Container>
        <Content>
          {htmlContent && (
            <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
          )}
          
          <NoDataMessage>
            <NoDataTitle>No Data Available</NoDataTitle>
            <NoDataText>
              Complete a journal entry with hours worked to see your optimal pace calculation.
            </NoDataText>
          </NoDataMessage>
          
          {/* CTA button appears right after content */}
          <ContinueButton onClick={() => onNext?.(data.key)}>
            {data.content?.ctaText || 'Continue'}
          </ContinueButton>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        {htmlContent && (
          <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        {/* Pricing Slider */}
        {renderPricingSlider()}
        
        {/* Win Rate Visualization */}
        {renderWinRateVisualization()}
        
        {/* Win Rate Improvement Message */}
        {selectedPrice > 0 && (
          <Description
            style={{ marginBottom: '15px' }}
            dangerouslySetInnerHTML={{
              __html: currentOptimalPace === 18
                ? "You've already unlocked <strong>100%</strong> of your total performance. We'll help you stay there as long as possible."
                : `This will <strong>increase my performance from ${Math.round((currentOptimalPace / 18) * 100)}% to ${Math.round((targetOptimalPace / 18) * 100)}%</strong> of my total potential ability.`
            }}
          />
        )}
        
        {/* CTA button appears right after content */}
        <ContinueButton 
          onClick={() => onNext?.(data.key)}
        >
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
      </Content>
    </Container>
  );
};

export default MembershipSelectionScreen;
